// The two sheets that give an Autotask category a dealer: link it to a dealer
// Omni already has (with an acknowledgment, and the stale-data path when someone
// saved first), or create a dealer from it. Ops only. Opened from Dealers and
// from the customers Review tab.
import React, { useState } from "react";
import type { Dealer, LensUser } from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { categoryLinkOf, type CategoryStatus } from "../api-customers.js";
import { Icon } from "../icons.js";
import { errorText, plural, stamp } from "../format.js";
import { useSession } from "../session.js";
import { Callout, Sheet, useToast } from "../ui.js";
import { CategoryName, personName, suggestDealer } from "./customer-bits.js";

function CategoryFacts({ category }: { category: CategoryStatus }): React.ReactElement {
  const preview = category.ownerPreview ?? null;
  return (
    <dl className="facts">
      <div>
        <dt>Autotask category</dt>
        <dd>
          <b>
            <CategoryName name={category.name} nickname={category.nickname} />
          </b>
          <span className="row-m">Category {category.id}</span>
        </dd>
      </div>
      <div>
        <dt>Active customers</dt>
        <dd>{category.customerCount.toLocaleString("en-US")}</dd>
      </div>
      {preview ? (
        <div>
          <dt>Most of them owned by</dt>
          <dd>
            {preview.name}
            <span className="row-m">
              {preview.companies.toLocaleString("en-US")} of {preview.ofCustomers.toLocaleString("en-US")} in Autotask
            </span>
          </dd>
        </div>
      ) : null}
    </dl>
  );
}

export interface LinkSheetProps {
  category: CategoryStatus;
  dealers: Dealer[];
  users: LensUser[] | null;
  /** How many of this category's customers wait for a dealer at the last sync, when the sync has read them. */
  waiting: number | null;
  /** The dealer to start with, from a suggestion. */
  dealerId: string | null;
  /** Reads dealers and categories again; resolves with what Omni holds now. */
  reload: () => Promise<{ dealers: Dealer[]; categories: CategoryStatus[] }>;
  onClose: () => void;
}

/** Links a category nobody holds to a dealer without one. */
export function LinkCategorySheet({ category, dealers, users, waiting, dealerId, reload, onClose }: LinkSheetProps): React.ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const suggestion = suggestDealer(category, dealers);
  const free = dealers.filter((d) => categoryLinkOf(d) === null).sort((a, b) => a.name.localeCompare(b.name));
  const [chosen, setChosen] = useState(dealerId ?? suggestion?.dealer.id ?? "");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState<{ holder: Dealer; by: string | null; at: string } | null>(null);
  const dealer = free.find((d) => d.id === chosen) ?? null;
  // The customers that move once a dealer holds it: those waiting at the last sync, else Autotask's count.
  const n = waiting ?? category.customerCount;
  const preview = category.ownerPreview ?? null;

  async function save(): Promise<void> {
    if (!dealer) return;
    setBusy(true);
    setError(null);
    try {
      await api.setDealerCategory(dealer.id, { categoryId: category.id, expectedDealerId: category.dealerId });
      await reload();
      toast(`Linked ${category.name} to ${dealer.name}. ${plural(n, "customer")} move under it on the next sync.`);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Someone else saved first: read again and say what Omni holds now.
        try {
          const now = await reload();
          const holder = now.dealers.find((d) => categoryLinkOf(d)?.id === category.id) ?? null;
          const link = holder ? categoryLinkOf(holder) : null;
          if (holder && link) {
            setStale({ holder, by: link.linkedBy, at: link.linkedAt });
            setBusy(false);
            return;
          }
        } catch (readErr) {
          setError(`${errorText(err)} Reading it again failed: ${errorText(readErr)}`);
          setBusy(false);
          return;
        }
      }
      setError(errorText(err));
      setBusy(false);
    }
  }

  if (stale) {
    return (
      <Sheet
        title={`Link ${category.name}`}
        onClose={onClose}
        foot={
          <button className="btn primary" type="button" onClick={onClose}>
            Close
          </button>
        }
      >
        <div className="stack" data-testid="link-stale">
          <div className="callout warn" role="status">
            <Icon name="triangle-alert" size={15} />
            <div>
              <p>
                <b>Someone saved first.</b> Omni read it again: {personName(users, stale.by)} linked {category.name} to {stale.holder.name} at {stamp(stale.at)}.
              </p>
              <p>
                Your save changed nothing. The {plural(n, "customer")} move under {stale.holder.name} on the next sync.
              </p>
            </div>
          </div>
          <CategoryFacts category={category} />
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={`Link ${category.name}`}
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="link-save" disabled={!dealer || !ack || busy} onClick={() => void save()}>
            {busy ? "Linking" : "Link category"}
          </button>
        </>
      }
    >
      <div className="stack" data-testid="link-sheet">
        <CategoryFacts category={category} />
        <label className="field">
          <span>Dealer</span>
          <select
            className="input"
            data-testid="link-dealer"
            value={chosen}
            onChange={(e) => {
              setChosen(e.target.value);
              setAck(false);
            }}
          >
            <option value="">Pick a dealer without a category</option>
            {free.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
                {suggestion?.dealer.id === d.id ? " (suggested)" : ""}
              </option>
            ))}
          </select>
        </label>
        {suggestion && dealer?.id === suggestion.dealer.id ? (
          <p className="field-note">Suggested because {suggestion.why} Names alone didn't match, so a person decides.</p>
        ) : null}
        {dealer ? (
          <>
            <h3>What linking does</h3>
            <ul className="does">
              <li>
                {plural(n, "customer")} in {category.name} move under {dealer.name} on the next sync.
              </li>
              <li>
                {dealer.autotaskOwner
                  ? `${dealer.autotaskOwner.name} stays ${dealer.name}'s account manager in Autotask.`
                  : preview
                    ? `${dealer.name} has no account manager in Autotask. The next sync sets ${preview.name}, who owns ${preview.companies} of them, unless ops set someone first.`
                    : `${dealer.name} has no account manager in Autotask. Set one in Dealers before it adds customers.`}
              </li>
              <li>
                New customers {dealer.name} adds go into Autotask under {category.name}.
              </li>
              <li>Nothing is written to Autotask: these customers already carry this category.</li>
            </ul>
            <label className="chk">
              <input type="checkbox" data-testid="link-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              <span>
                {dealer.name}'s people will see these {plural(n, "customer")}, with their quotes and orders. I checked they belong to {dealer.name}.
              </span>
            </label>
          </>
        ) : free.length === 0 ? (
          <Callout kind="warn" icon="triangle-alert">
            Every dealer already holds a category. Create a dealer from this category instead.
          </Callout>
        ) : null}
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

export interface CreateDealerSheetProps {
  categories: CategoryStatus[];
  /** Waiting customers per category id at the last sync. */
  waiting: Record<string, number>;
  categoryId: number | null;
  reload: () => Promise<unknown>;
  onClose: () => void;
}

/** Creates a dealer named after an Autotask category nobody holds, linked to it. */
export function CreateDealerSheet({ categories, waiting, categoryId, reload, onClose }: CreateDealerSheetProps): React.ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const open = categories.filter((c) => c.active && !c.dealerId).sort((a, b) => a.id - b.id);
  const [chosen, setChosen] = useState(categoryId !== null ? String(categoryId) : String(open[0]?.id ?? ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const category = open.find((c) => String(c.id) === chosen) ?? null;
  const n = category ? waiting[String(category.id)] ?? category.customerCount : 0;
  const preview = category?.ownerPreview ?? null;

  async function create(): Promise<void> {
    if (!category) return;
    setBusy(true);
    setError(null);
    try {
      const dealer = await api.dealerFromCategory(category.id);
      await reload();
      toast(`Created ${dealer.name}. Its ${plural(n, "customer")} move under it on the next sync.`);
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet
      title="Create a dealer from a category"
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="create-dealer-save" disabled={!category || busy} onClick={() => void create()}>
            {busy ? "Creating" : "Create dealer"}
          </button>
        </>
      }
    >
      <div className="stack" data-testid="create-dealer-sheet">
        <Callout icon="info">
          Omni can't create Autotask categories. If the dealer's category isn't listed, a person adds it in Autotask admin first; it shows up here on the next sync.
        </Callout>
        <label className="field">
          <span>Autotask category</span>
          <select className="input" data-testid="create-dealer-category" value={chosen} onChange={(e) => setChosen(e.target.value)}>
            {open.length === 0 ? <option value="">Every active category has a dealer</option> : null}
            {open.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
                {c.nickname ? ` (${c.nickname})` : ""}
                {` \u00b7 ${plural(waiting[String(c.id)] ?? c.customerCount, "customer")}`}
              </option>
            ))}
          </select>
        </label>
        {category ? (
          <>
            <h3>The new dealer</h3>
            <dl className="facts">
              <div>
                <dt>Name</dt>
                <dd>{category.name}</dd>
              </div>
              <div>
                <dt>Pricing</dt>
                <dd>Dealer</dd>
              </div>
              <div>
                <dt>NS reseller key</dt>
                <dd className="muted">None yet</dd>
              </div>
              <div>
                <dt>Account manager in Autotask</dt>
                <dd>
                  {preview ? (
                    <>
                      {preview.name}
                      <span className="row-m">
                        {`Set on the next sync \u00b7 owns ${preview.companies} of ${preview.ofCustomers}`}
                      </span>
                    </>
                  ) : (
                    <span className="muted">None until ops choose one</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Customers</dt>
                <dd>
                  {n.toLocaleString("en-US")}
                  <span className="row-m">Move under it on the next sync</span>
                </dd>
              </div>
            </dl>
            <p className="field-note">Invite its people from Settings once it exists.</p>
          </>
        ) : null}
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}
